const preventPolishOrphans = (root = document.body) => {
  if (!root) return;
  const ignored =
    "script, style, noscript, textarea, input, select, option, pre, code, [contenteditable], [data-allow-orphans]";
  const textNodes = [];
  if (root.nodeType === Node.TEXT_NODE) textNodes.push(root);
  else {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) textNodes.push(walker.currentNode);
  }

  textNodes.forEach((node) => {
    if (!node.parentElement || node.parentElement.closest(ignored)) return;
    node.data = node.data.replace(/(^|\s)([aiouwz])\s+(?=\S)/giu, "$1$2\u00a0");
  });
};

class ChataTheme {
  constructor() {
    this.syncAnnouncementHeight();
    this.bindProductForms();
    this.bindCartControls();
    this.bindMobileMenu();
    this.bindDesktopNavigation();
  }

  syncAnnouncementHeight() {
    const announcement = document.querySelector(".cp-announcement");
    const sync = () => {
      const height = announcement?.getBoundingClientRect().height || 0;
      document.documentElement.style.setProperty(
        "--cp-announcement-height",
        `${height}px`,
      );
    };
    sync();
    window.addEventListener("resize", sync, { passive: true });
    if (announcement && "ResizeObserver" in window) {
      this.announcementObserver = new ResizeObserver(sync);
      this.announcementObserver.observe(announcement);
    }
  }

  bindProductForms() {
    document.querySelectorAll("[data-product-form]").forEach((form) => {
      const selector = form.querySelector("[data-variant-select]");
      const button = form.querySelector("[data-add-to-cart]");
      if (!selector || !button) return;

      selector.addEventListener("change", () => {
        const option = selector.selectedOptions[0];
        button.disabled = option.dataset.available !== "true";
        button.textContent = button.disabled
          ? "Niedostępny"
          : "Dodaj do koszyka";
      });
    });
  }

  bindCartControls() {
    const shopRoot = () => window.Shopify?.routes?.root || "/";
    const announce = (message) => {
      const region = document.querySelector("[data-live-region]");
      if (region) region.textContent = message;
    };
    document.querySelectorAll("[data-cart-form]").forEach((form) => {
      let updateTimer;
      const queueCartUpdate = () => {
        window.clearTimeout(updateTimer);
        updateTimer = window.setTimeout(() => {
          const updateMarker = document.createElement("input");
          updateMarker.type = "hidden";
          updateMarker.name = "update";
          updateMarker.value = "Aktualizuj koszyk";
          form.append(updateMarker);
          HTMLFormElement.prototype.submit.call(form);
        }, 420);
      };

      form.addEventListener("click", (event) => {
        const decrease = event.target.closest("[data-cart-quantity-minus]");
        const increase = event.target.closest("[data-cart-quantity-plus]");
        if (!decrease && !increase) return;
        const control = event.target.closest(".cp-cart-quantity");
        const input = control?.querySelector("[data-cart-quantity]");
        if (!input) return;
        const change = increase ? 1 : -1;
        input.value = String(Math.max(0, Number(input.value || 0) + change));
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });

      form.addEventListener("change", (event) => {
        if (event.target.matches("[data-cart-quantity]")) queueCartUpdate();
      });

      const discountInput = form.querySelector("[data-cart-discount-code]");
      const discountButton = form.querySelector("[data-cart-discount-apply]");
      const discountMessage = form.querySelector(
        "[data-cart-discount-message]",
      );
      const applyDiscount = async () => {
        const code = discountInput?.value.trim();
        if (!code) {
          if (discountMessage)
            discountMessage.textContent = "Wpisz kod rabatowy.";
          discountInput?.focus();
          return;
        }

        window.clearTimeout(updateTimer);
        discountButton.disabled = true;
        if (discountMessage)
          discountMessage.textContent = "Sprawdzamy kod rabatowy…";
        const updates = {};
        form.querySelectorAll("[data-cart-quantity]").forEach((input) => {
          updates[input.dataset.cartLineKey] = Math.max(
            0,
            Number(input.value || 0),
          );
        });

        try {
          const response = await fetch(`${shopRoot()}cart/update.js`, {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              updates,
              note: form.querySelector("[name='note']")?.value || "",
            }),
          });
          if (!response.ok) throw new Error("Nie udało się zapisać koszyka.");
          const cartUrl = `${shopRoot()}cart`;
          window.location.assign(
            `${shopRoot()}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(cartUrl)}`,
          );
        } catch (error) {
          const message =
            error.message || "Nie udało się zastosować kodu rabatowego.";
          if (discountMessage) discountMessage.textContent = message;
          announce(message);
          discountButton.disabled = false;
        }
      };
      discountButton?.addEventListener("click", applyDiscount);
      discountInput?.addEventListener("keydown", (event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        applyDiscount();
      });
    });
  }

  bindMobileMenu() {
    const menu = document.querySelector("[data-mobile-menu]");
    if (!menu) return;
    const summary = menu.querySelector(":scope > summary");
    const drawer = menu.querySelector(".cp-mobile-nav__drawer");
    let closeTimer;

    const finishClose = (restoreFocus = true) => {
      window.clearTimeout(closeTimer);
      menu.classList.remove("is-visible", "is-closing");
      menu.open = false;
      summary?.setAttribute("aria-expanded", "false");
      summary?.setAttribute("aria-label", "Otwórz menu");
      document.body.classList.remove("is-locked");
      if (restoreFocus) summary?.focus({ preventScroll: true });
    };

    const close = (restoreFocus = true) => {
      if (!menu.open || menu.classList.contains("is-closing")) return;
      if (matchMedia("(prefers-reduced-motion: reduce)").matches) {
        finishClose(restoreFocus);
        return;
      }
      menu.classList.remove("is-visible");
      menu.classList.add("is-closing");
      closeTimer = window.setTimeout(() => finishClose(restoreFocus), 460);
    };

    summary?.addEventListener("click", (event) => {
      if (!menu.open) return;
      event.preventDefault();
      close();
    });

    menu.addEventListener("toggle", () => {
      if (!menu.open) return;
      window.clearTimeout(closeTimer);
      menu.classList.remove("is-closing");
      summary?.setAttribute("aria-expanded", "true");
      summary?.setAttribute("aria-label", "Zamknij menu");
      document.body.classList.add("is-locked");
      requestAnimationFrame(() =>
        requestAnimationFrame(() => menu.classList.add("is-visible")),
      );
    });

    menu
      .querySelectorAll("[data-mobile-menu-close]")
      .forEach((control) => control.addEventListener("click", () => close()));
    menu
      .querySelectorAll("[data-footer-link]")
      .forEach((control) =>
        control.addEventListener("click", () => finishClose(false)),
      );

    menu.addEventListener("keydown", (event) => {
      if (event.key === "Escape") close();
      if (event.key !== "Tab" || !drawer || !menu.open) return;
      const focusable = [
        ...drawer.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    matchMedia("(min-width: 990px)").addEventListener("change", (event) => {
      if (event.matches && menu.open) finishClose(false);
    });
  }

  bindDesktopNavigation() {
    const branches = document.querySelectorAll(".cp-nav-branch");
    branches.forEach((branch) => {
      branch.addEventListener("keydown", (event) => {
        if (event.key !== "Escape") return;
        branch.open = false;
        branch.querySelector(":scope > summary")?.focus();
      });

      const preview = branch.querySelector(".cp-nav-children__preview img");
      const caption = branch.querySelector("[data-cp-nav-preview-title]");
      if (!preview) return;
      let swapTimer;
      branch.querySelectorAll("[data-cp-nav-preview]").forEach((link) => {
        const update = () => {
          const source = link.dataset.image;
          if (!source || preview.src === source) return;
          window.clearTimeout(swapTimer);
          preview.classList.add("is-changing");
          const preload = new Image();
          preload.addEventListener("load", () => {
            swapTimer = window.setTimeout(() => {
              preview.removeAttribute("srcset");
              preview.src = source;
              preview.alt = link.dataset.title || "";
              if (caption) caption.textContent = link.dataset.title || "";
              requestAnimationFrame(() =>
                preview.classList.remove("is-changing"),
              );
            }, 90);
          });
          preload.src = source;
        };
        link.addEventListener("mouseenter", update);
        link.addEventListener("focus", update);
      });
    });

    document.addEventListener("click", (event) => {
      branches.forEach((branch) => {
        if (!branch.contains(event.target)) branch.open = false;
      });
    });
  }
}

class NewsletterPopup extends HTMLElement {
  connectedCallback() {
    this.storageKey = `chata-newsletter-${this.dataset.sectionId}`;
    this.dialog = this.querySelector(".cp-newsletter-popup__dialog");
    this.closeControls = this.querySelectorAll("[data-newsletter-close]");
    this.success = this.querySelector("[data-newsletter-success]");
    this.previouslyFocused = null;
    this.closeControls.forEach((control) =>
      control.addEventListener("click", () => this.close()),
    );
    this.addEventListener("keydown", (event) => this.handleKeydown(event));

    if (this.success) {
      this.open();
      this.success.focus();
      this.rememberDismissal();
      return;
    }

    if (window.Shopify?.designMode) return;
    if (!this.canOpen()) return;
    this.openTimer = window.setTimeout(
      () => this.open(),
      Number(this.dataset.delay || 0),
    );
  }

  disconnectedCallback() {
    window.clearTimeout(this.openTimer);
  }

  canOpen() {
    try {
      const dismissedAt = Number(localStorage.getItem(this.storageKey));
      if (!dismissedAt) return true;
      if (this.dataset.frequency === "never") return false;
      const interval = Number(this.dataset.frequency || 14) * 86400000;
      return Date.now() - dismissedAt >= interval;
    } catch (_) {
      return true;
    }
  }

  rememberDismissal() {
    try {
      localStorage.setItem(this.storageKey, String(Date.now()));
    } catch (_) {
      // Storage may be unavailable in privacy mode; closing still works.
    }
  }

  open() {
    window.clearTimeout(this.openTimer);
    if (!this.hidden) return;
    this.previouslyFocused = document.activeElement;
    this.hidden = false;
    document.body.classList.add("is-locked");
    requestAnimationFrame(() => {
      this.classList.add("is-visible");
      this.querySelector("input:not([type='hidden'])")?.focus();
    });
  }

  close() {
    if (this.hidden) return;
    this.rememberDismissal();
    this.classList.remove("is-visible");
    document.body.classList.remove("is-locked");
    const finish = () => {
      this.hidden = true;
      this.previouslyFocused?.focus?.({ preventScroll: true });
    };
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) finish();
    else window.setTimeout(finish, 240);
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close();
      return;
    }
    if (event.key !== "Tab" || !this.dialog) return;
    const focusable = [
      ...this.dialog.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
      ),
    ];
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get("newsletter-popup")) {
  customElements.define("newsletter-popup", NewsletterPopup);
}

document.addEventListener("DOMContentLoaded", () => {
  new ChataTheme();
  preventPolishOrphans();
  new MutationObserver((records) => {
    records.forEach((record) =>
      record.addedNodes.forEach((node) => preventPolishOrphans(node)),
    );
  }).observe(document.body, { childList: true, subtree: true });
});

document.addEventListener("shopify:section:select", (event) => {
  event.target.querySelector("newsletter-popup")?.open();
});
